<template>
  <div class="settings-page">
    <DashboardPageHeader
      title="Settings"
      subtitle="Update your branding, rewards card and customer sign-up page."
    >
      <template v-if="!loading && canEdit" #actions>
        <v-btn
          variant="text"
          color="primary"
          :disabled="!isDirty || saving"
          @click="onDiscard"
        >
          Discard changes
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!canSave || saving"
          @click="onSave"
        >
          Save changes
        </v-btn>
      </template>
    </DashboardPageHeader>

    <div v-if="loading" class="settings-loading">
      <BrandedLoader text="Loading your settings..." spinner-color="primary" />
    </div>

    <div v-else-if="loadError" class="settings-empty" role="alert">
      <v-icon class="settings-empty-icon">mdi-alert-circle-outline</v-icon>
      <h2>We couldn't load this business</h2>
      <p>{{ loadError }}</p>
      <v-btn
        class="mt-3"
        color="primary"
        variant="flat"
        prepend-icon="mdi-refresh"
        @click="load"
      >
        Try again
      </v-btn>
    </div>

    <template v-else>
      <div v-if="!canEdit" class="settings-readonly-banner" role="status">
        <v-icon class="settings-readonly-icon">mdi-lock-outline</v-icon>
        <div>
          <strong>You're viewing in read-only mode.</strong>
          Only the owner or an administrator can change your settings.
        </div>
      </div>

      <div class="settings-status-row">
        <div v-if="saveError" class="settings-status settings-status--error" role="alert">
          <v-icon class="settings-status-icon">mdi-alert-circle</v-icon>
          {{ saveError }}
        </div>
        <div
          v-else-if="saveSuccess"
          class="settings-status settings-status--success"
          role="status"
        >
          <v-icon class="settings-status-icon">mdi-check-circle</v-icon>
          Settings saved.
        </div>
        <div
          v-else-if="isDirty && canEdit"
          class="settings-status settings-status--dirty"
          role="status"
        >
          You have unsaved changes.
        </div>
      </div>

      <div class="settings-tabs">
      <!-- The tab bar is constrained to the editor column width (via the same
           grid template as the panel below) so the active tab sits directly on
           top of the editor card and never overhangs the transparent preview
           column. -->
      <div class="settings-tabwrap">
      <div class="settings-tab-bar" role="tablist" aria-label="Settings sections">
        <button
          class="settings-tab"
          :class="{ 'settings-tab--active': activeTab === 'card' }"
          role="tab"
          :aria-selected="activeTab === 'card'"
          :tabindex="activeTab === 'card' ? 0 : -1"
          id="tab-card"
          aria-controls="panel-card"
          @click="activeTab = 'card'"
        >
          <span class="settings-tab-label">
            <v-icon class="settings-tab-icon">mdi-card-account-details-outline</v-icon>
            <span>Brand &amp; rewards card</span>
          </span>
          <span v-if="cardTabHasError" class="settings-tab-dot" aria-hidden="true"></span>
        </button>
        <button
          class="settings-tab"
          :class="{ 'settings-tab--active': activeTab === 'signup' }"
          role="tab"
          :aria-selected="activeTab === 'signup'"
          :tabindex="activeTab === 'signup' ? 0 : -1"
          id="tab-signup"
          aria-controls="panel-signup"
          @click="activeTab = 'signup'"
        >
          <span class="settings-tab-label">
            <v-icon class="settings-tab-icon">mdi-web</v-icon>
            <span>Customer sign-up page</span>
          </span>
        </button>
      </div>
      </div>

      <!-- Panel: Brand & rewards card -->
      <div
        v-show="activeTab === 'card'"
        id="panel-card"
        role="tabpanel"
        aria-labelledby="tab-card"
        class="settings-tab-panel"
      >
        <div class="settings-grid">
          <section class="settings-editor-pane">
            <p class="settings-tab-subtitle">
              Update the logo, colours and rewards your customers see on their
              card.
            </p>
            <div class="settings-editor-wrap">
              <BrandingEditor
                :model-value="brandingSlice"
                :initial-logo-url="logoUrl"
                :readonly="!canEdit"
                :hide-display-name="false"
                show-start-stamp-count
                @update:model-value="onBrandingUpdate"
                @update:logo-file="onLogoFile"
                @logo-error="onLogoError"
              />
            </div>
          </section>

          <aside class="settings-preview-pane">
            <div class="settings-preview-sticky">
              <RewardsCardPreview
                :display-name="form.displayName"
                :logo-url="cardLogoUrl"
                :background-color="form.backgroundColor"
                :foreground-color="form.foregroundColor"
                :threshold-for-redeeming="Number(form.thresholdForRedeeming)"
                :auxiliary-field-label="form.auxiliaryFieldLabel || null"
                :auxiliary-field-value="form.auxiliaryFieldValue || null"
              />
              <p class="settings-preview-caption">Live card preview</p>
              <p class="settings-preview-disclaimer">
                This is a rough mockup for illustrative purposes - your
                customer's actual rewards card will use native dimensions that
                may differ from what you see here.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <!-- Panel: Customer sign-up page -->
      <div
        v-show="activeTab === 'signup'"
        id="panel-signup"
        role="tabpanel"
        aria-labelledby="tab-signup"
        class="settings-tab-panel"
      >
        <div class="settings-grid">
          <section class="settings-editor-pane">
            <p class="settings-tab-subtitle">
              Edit the page customers see when they join your loyalty scheme.
            </p>
            <div class="settings-editor-wrap">
              <div class="settings-field-block">
                <label class="settings-block-label" for="settings-title">Heading</label>
                <p class="settings-block-hint">
                  The headline customers see when they join - keep it short and
                  warm.
                </p>
                <v-text-field
                  id="settings-title"
                  :model-value="form.title"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  placeholder="Welcome to our rewards programme!"
                  :readonly="!canEdit"
                  @update:model-value="onFieldUpdate('title', $event)"
                />
              </div>

              <div class="settings-field-block">
                <label class="settings-block-label" for="settings-subtitle">Subtitle</label>
                <p class="settings-block-hint">
                  One sentence explaining the reward (e.g. "Get every 10th coffee
                  free.").
                </p>
                <v-textarea
                  id="settings-subtitle"
                  :model-value="form.subtitle"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  rows="3"
                  auto-grow
                  placeholder="Join our rewards scheme and get every 10th drink free."
                  :readonly="!canEdit"
                  @update:model-value="onFieldUpdate('subtitle', $event)"
                />
              </div>

              <p class="settings-meta-hint">
                Your sign-up page uses the same logo and colours as your card.
              </p>
            </div>
          </section>

          <aside class="settings-preview-pane">
            <div class="settings-preview-sticky">
              <WebDisplayPreview
                :title="form.title"
                :subtitle="form.subtitle"
                :logo-url="cardLogoUrl"
                :background-color="form.backgroundColor"
                :text-color="form.foregroundColor"
              />
              <p class="settings-preview-caption">Live sign-up page preview</p>
              <p class="settings-preview-disclaimer">
                This is a rough mockup for illustrative purposes - your
                customer's actual sign-up page may differ from what you see
                here.
              </p>
            </div>
          </aside>
        </div>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
import { computed, inject, onMounted, onUnmounted, ref, watch } from "vue";
import BrandingEditor from "@/components/Organisms/BrandingEditor.vue";
import RewardsCardPreview from "@/components/Organisms/RewardsCardPreview.vue";
import WebDisplayPreview from "@/components/Organisms/WebDisplayPreview.vue";
import DashboardPageHeader from "@/components/Molecules/DashboardPageHeader.vue";
import BrandedLoader from "@/components/Molecules/BrandedLoader.vue";
import {
  loadBusinessSettings,
  saveBusinessSettings,
} from "@/settingsHandler.js";
import { track } from "@/analytics.js";

const DEFAULT_FORM = {
  displayName: "",
  backgroundColor: "#ffffff",
  foregroundColor: "#000000",
  thresholdForRedeeming: 8,
  startStampCount: 0,
  auxiliaryFieldLabel: "",
  auxiliaryFieldValue: "",
  title: "",
  subtitle: "",
};

const cloneForm = (source) => ({ ...DEFAULT_FORM, ...source });

export default {
  name: "DashboardSettingsComponent",
  components: {
    BrandingEditor,
    RewardsCardPreview,
    WebDisplayPreview,
    DashboardPageHeader,
    BrandedLoader,
  },
  setup() {
    const selectedBusinessId = inject("selectedBusinessId");

    // Local UI state only - which tab is shown. The form/isDirty/canSave are
    // unified across both tabs, so this never touches save logic.
    const activeTab = ref("card");

    const loading = ref(true);
    const loadError = ref("");
    const saving = ref(false);
    const saveError = ref("");
    const saveSuccess = ref(false);
    const form = ref(cloneForm());
    const originalForm = ref(cloneForm());
    const role = ref("Employee");
    const logoUrl = ref("");
    const pendingLogoFile = ref(null);

    // Object URL for the newly-picked logo so the side previews reflect it
    // live. Revoked whenever it's replaced or the component unmounts so we
    // never leak blobs.
    const pendingLogoPreviewUrl = ref("");
    const revokePendingPreview = () => {
      if (pendingLogoPreviewUrl.value) {
        URL.revokeObjectURL(pendingLogoPreviewUrl.value);
        pendingLogoPreviewUrl.value = "";
      }
    };

    const canEdit = computed(
      () => role.value === "Owner" || role.value === "Administrator"
    );

    // Logo shown in the side previews: the freshly-picked file when present,
    // otherwise the remote logo.
    const cardLogoUrl = computed(
      () => pendingLogoPreviewUrl.value || logoUrl.value || ""
    );

    // Slice of the form bound to BrandingEditor (it owns the display name and
    // branding fields, not the sign-up copy).
    const brandingSlice = computed(() => ({
      displayName: form.value.displayName,
      backgroundColor: form.value.backgroundColor,
      foregroundColor: form.value.foregroundColor,
      thresholdForRedeeming: form.value.thresholdForRedeeming,
      startStampCount: form.value.startStampCount,
      auxiliaryFieldLabel: form.value.auxiliaryFieldLabel,
      auxiliaryFieldValue: form.value.auxiliaryFieldValue,
    }));

    const FORM_KEYS = [
      "displayName",
      "backgroundColor",
      "foregroundColor",
      "thresholdForRedeeming",
      "startStampCount",
      "auxiliaryFieldLabel",
      "auxiliaryFieldValue",
      "title",
      "subtitle",
    ];

    const isDirty = computed(() => {
      if (pendingLogoFile.value !== null) return true;
      return FORM_KEYS.some(
        (key) => form.value[key] !== originalForm.value[key]
      );
    });

    // Start stamps are a head start on the card, so 0..threshold. The editor
    // clamps as the threshold moves, so this only catches a value that arrived
    // out of range (e.g. a threshold lowered from another client).
    const startStampCountInvalid = () => {
      const threshold = Number(form.value.thresholdForRedeeming);
      const start = Number(form.value.startStampCount);
      if (!Number.isFinite(start) || start < 0) return true;
      return Number.isFinite(threshold) && start > threshold;
    };

    const canSave = computed(() => {
      if (!canEdit.value || !isDirty.value) return false;
      if (!String(form.value.displayName || "").trim()) return false;
      const hasLabel = !!String(form.value.auxiliaryFieldLabel || "").trim();
      const hasValue = !!String(form.value.auxiliaryFieldValue || "").trim();
      if (hasLabel !== hasValue) return false;
      const threshold = Number(form.value.thresholdForRedeeming);
      if (!Number.isFinite(threshold) || threshold < 6 || threshold > 14) {
        return false;
      }
      if (startStampCountInvalid()) return false;
      return true;
    });

    // Whether the card tab holds a save-blocking field error. Mirrors the
    // card-side clauses of canSave so the tab can show a warning dot even when
    // the user is on the sign-up tab. The sign-up tab has no save-blocking
    // field today, so only the card tab gets a dot.
    const cardTabHasError = computed(() => {
      if (!canEdit.value) return false;
      if (!String(form.value.displayName || "").trim()) return true;
      const hasLabel = !!String(form.value.auxiliaryFieldLabel || "").trim();
      const hasValue = !!String(form.value.auxiliaryFieldValue || "").trim();
      if (hasLabel !== hasValue) return true;
      const threshold = Number(form.value.thresholdForRedeeming);
      if (!Number.isFinite(threshold) || threshold < 6 || threshold > 14) {
        return true;
      }
      if (startStampCountInvalid()) return true;
      return false;
    });

    // Monotonic token guarding against overlapping loads. When the user
    // switches business rapidly, an earlier-started fetch can resolve after a
    // later one - the token ensures only the most recent load applies its
    // result (or its error / loading reset).
    let loadToken = 0;

    const load = async () => {
      const id = selectedBusinessId?.value;
      if (!id) {
        loading.value = false;
        return;
      }
      const token = ++loadToken;
      loading.value = true;
      loadError.value = "";
      saveError.value = "";
      saveSuccess.value = false;
      revokePendingPreview();
      pendingLogoFile.value = null;
      try {
        const { form: loadedForm, role: loadedRole, logoUrl: loadedLogoUrl } =
          await loadBusinessSettings(id);
        if (token !== loadToken) return;
        role.value = loadedRole || "Employee";
        form.value = cloneForm(loadedForm);
        originalForm.value = cloneForm(loadedForm);
        logoUrl.value = loadedLogoUrl || "";
        track("dashboard_settings_viewed", { business_id: id });
      } catch (e) {
        if (token !== loadToken) return;
        loadError.value =
          "Something went wrong loading your settings. Please refresh and try again.";
      } finally {
        if (token === loadToken) loading.value = false;
      }
    };

    onMounted(load);
    watch(() => selectedBusinessId?.value, load);
    onUnmounted(revokePendingPreview);

    const onFieldUpdate = (key, value) => {
      form.value = { ...form.value, [key]: value };
      saveSuccess.value = false;
    };

    // BrandingEditor emits a whole-object replace of its slice; merge it back
    // into the full form.
    const onBrandingUpdate = (slice) => {
      form.value = { ...form.value, ...slice };
      saveSuccess.value = false;
    };

    const onLogoFile = (file) => {
      revokePendingPreview();
      pendingLogoFile.value = file || null;
      if (file) {
        pendingLogoPreviewUrl.value = URL.createObjectURL(file);
      }
      saveSuccess.value = false;
    };

    const onLogoError = () => {
      // BrandingEditor surfaces the error inline; clearing the success banner
      // keeps the status row honest.
      saveSuccess.value = false;
    };

    const onDiscard = () => {
      form.value = cloneForm(originalForm.value);
      revokePendingPreview();
      pendingLogoFile.value = null;
      saveError.value = "";
      saveSuccess.value = false;
    };

    const onSave = async () => {
      if (!canEdit.value) {
        saveError.value =
          "Only the owner or an administrator can change your settings.";
        return;
      }
      const id = selectedBusinessId?.value;
      if (!id) return;
      saving.value = true;
      saveError.value = "";
      saveSuccess.value = false;
      try {
        await saveBusinessSettings(
          id,
          form.value,
          pendingLogoFile.value,
          originalForm.value
        );
        // The writes targeted `id` correctly, but if the user switched
        // business mid-save we must not stamp the now-loaded (different)
        // business's form as saved. The new business owns the UI state.
        if (selectedBusinessId?.value !== id) return;
        originalForm.value = cloneForm(form.value);
        revokePendingPreview();
        pendingLogoFile.value = null;
        saveSuccess.value = true;
      } catch (e) {
        if (selectedBusinessId?.value !== id) return;
        saveError.value =
          "We couldn't save your settings. Please try again in a moment.";
      } finally {
        saving.value = false;
      }
    };

    return {
      activeTab,
      cardTabHasError,
      loading,
      loadError,
      saving,
      saveError,
      saveSuccess,
      form,
      role,
      canEdit,
      isDirty,
      canSave,
      logoUrl,
      cardLogoUrl,
      brandingSlice,
      load,
      onFieldUpdate,
      onBrandingUpdate,
      onLogoFile,
      onLogoError,
      onDiscard,
      onSave,
    };
  },
};
</script>

<style scoped>
.settings-page :deep(.v-btn) {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border-radius: 999px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Primary green pill - matches the header "Get started" button. */
.settings-page :deep(.v-btn--variant-flat.text-primary),
.settings-page :deep(.v-btn.bg-primary) {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
}

.settings-page :deep(.v-btn--variant-flat.text-primary:hover),
.settings-page :deep(.v-btn.bg-primary:hover) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

/* Secondary outlined buttons - same hover pop, lighter shadow. */
.settings-page :deep(.v-btn--variant-outlined.text-primary) {
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.1) !important;
}

.settings-page :deep(.v-btn--variant-outlined.text-primary:hover) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.2) !important;
  transform: translateY(-1px);
}

/* Ghost / text buttons (Discard changes) - pop on hover without a fill. */
.settings-page :deep(.v-btn--variant-text.text-primary:hover) {
  transform: translateY(-1px);
}

/* Disabled state must not lift. */
.settings-page :deep(.v-btn--disabled:hover) {
  transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.1) !important;
}

.settings-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.settings-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.settings-empty {
  text-align: center;
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 48px 24px;
}

.settings-empty-icon {
  color: var(--lulu-text-subtle);
  font-size: 48px !important;
  margin-bottom: 12px;
}

.settings-readonly-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--lulu-surface);
  border: 1px solid var(--lulu-border);
  border-radius: var(--lulu-radius-card);
  padding: 12px 16px;
  color: var(--lulu-text-muted);
  font-size: 13px;
  line-height: 1.5;
}

.settings-readonly-icon {
  color: var(--lulu-text-muted) !important;
}

.settings-status-row {
  min-height: 0;
}

.settings-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
}

.settings-status--success {
  background: var(--lulu-primary-soft);
  color: var(--lulu-primary);
}

.settings-status--error {
  background: var(--lulu-danger-soft);
  color: var(--lulu-danger);
}

.settings-status--dirty {
  background: var(--lulu-warning-soft);
  color: var(--lulu-warning-text);
}

.settings-status-icon {
  font-size: 16px !important;
}

/* Chrome-style tabs replacing the two stacked sections. The tab bar is held
   to the editor column width (mirrors the panel grid below) so the active tab
   lands squarely on the editor card and never overhangs the preview column. */
.settings-tabwrap {
  display: grid;
  grid-template-columns: 1fr;
}

@media (min-width: 960px) {
  .settings-tabwrap {
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 24px;
  }
}

.settings-tab-bar {
  display: flex;
  gap: 4px;
  align-items: flex-end;
}

.settings-tab {
  position: relative;
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 16px;
  font-size: 15px;
  font-weight: 600;
  color: var(--lulu-text-muted);
  background: transparent;
  /* Keep an explicit transparent bottom border (rather than `border-bottom:
     none`, which resets the bottom border-color to currentColor/gray). That
     reset made the border-color transition flash a temporary gray line at the
     tab's base when activating. */
  border: 1px solid transparent;
  /* Unselected: the exposed outer top corner matches the (square) bottom
     corners. Only the active tab gets the rounded folder-tab top. */
  border-radius: 0;
  margin-bottom: -1px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.18s, color 0.18s, border-color 0.18s;
}

/* No focus/tap outline for pointer taps (it showed as a temporary gray line at
   the tab's base); keep a visible ring only for keyboard navigation. */
.settings-tab:focus:not(:focus-visible) {
  outline: none;
}

.settings-tab:focus-visible {
  outline: 2px solid var(--lulu-primary);
  outline-offset: -2px;
}

/* Hover grows the icon + label as one unit (so the spacing between them is
   preserved), not the tab box itself. */
.settings-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.18s;
  transform-origin: center;
}

.settings-tab:hover .settings-tab-label {
  transform: scale(1.08);
}

.settings-tab--active {
  color: var(--lulu-primary);
  background: var(--lulu-surface);
  border-bottom: 1px solid var(--lulu-surface);
  border-radius: 10px 10px 0 0;
  z-index: 1;
}

/* Concave fillet sweeping the active tab's inner base smoothly into the white
   surface below, mirroring the rounded outer top corner so the inner corner
   reads as a smooth curve instead of a hard 90deg notch. The transparent
   quarter reveals the page background; the rest matches the surface. */
.settings-tab--active::after {
  content: "";
  position: absolute;
  bottom: 0;
  width: 10px;
  height: 10px;
  pointer-events: none;
}

/* Signup (right) tab active -> inner side is on the left. */
#tab-signup.settings-tab--active::after {
  left: -10px;
  background: radial-gradient(circle at top left, transparent 10px, var(--lulu-surface) 10.5px);
}

/* Card (left) tab active -> inner side is on the right. */
#tab-card.settings-tab--active::after {
  right: -10px;
  background: radial-gradient(circle at top right, transparent 10px, var(--lulu-surface) 10.5px);
}

.settings-tab-icon {
  font-size: 20px !important;
}

.settings-tab-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--lulu-warning-text);
  flex-shrink: 0;
}

/* Sits as the first line inside the editor card, directly under the tabs, so
   the contextual copy lives on the connected surface (not in the gap). */
.settings-tab-subtitle {
  font-size: 13px;
  color: var(--lulu-text-muted);
  line-height: 1.5;
  margin: 0 0 16px;
}

/* Flush against the tab bar so the active tab fuses into the editor card. */
.settings-tab-panel {
  margin-top: 0;
}

/* Narrow screens: the editor + stacked preview share one continuous white
   surface that runs from directly under the tabs to the bottom of the panel,
   so the live preview sits on top of the white background (not the page bg).
   The active tab fuses into this surface via its square top + rounded folder
   top corners. */
@media (max-width: 959px) {
  .settings-tab-panel {
    background: var(--lulu-surface);
    box-shadow: var(--lulu-card-shadow);
    padding: 24px;
    overflow: hidden;
  }
  /* The top corner under the *inactive* tab is exposed, so round it to mirror
     the active tab's rounded top. The corner under the active tab stays square
     and is hidden as the active tab fuses into this surface. Each panel only
     renders when its own tab is active, so the radii can be fixed per panel:
     card panel => left tab active (square left, round right); signup panel =>
     right tab active (round left, square right). */
  #panel-card.settings-tab-panel {
    border-radius: 0 10px var(--lulu-radius-card) var(--lulu-radius-card);
  }
  #panel-signup.settings-tab-panel {
    border-radius: 10px 0 var(--lulu-radius-card) var(--lulu-radius-card);
  }
}

@media (max-width: 480px) {
  .settings-tab {
    font-size: 13px;
    padding: 10px 8px;
  }
}

.settings-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;
}

@media (min-width: 960px) {
  .settings-grid {
    grid-template-columns: minmax(0, 1fr) 320px;
  }
}

.settings-editor-pane {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 24px;
  min-width: 0;
}

/* On desktop the tab bar sits directly on top of the editor card. The top
   corner under the *active* tab is flattened so the tab fuses into the card as
   one continuous surface; the corner under the *inactive* tab stays rounded
   (mirroring the active tab's top). Each panel only renders when its own tab is
   active, so the radii can be fixed per panel: card panel => left tab active
   (square left, round right); signup panel => right tab active (round left,
   square right). On mobile this is handled by the shared panel surface instead. */
@media (min-width: 960px) {
  #panel-card .settings-editor-pane {
    border-radius: 0 10px var(--lulu-radius-card) var(--lulu-radius-card);
  }
  #panel-signup .settings-editor-pane {
    border-radius: 10px 0 var(--lulu-radius-card) var(--lulu-radius-card);
  }
}

.settings-editor-wrap {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.settings-field-block {
  display: flex;
  flex-direction: column;
}

.settings-block-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-primary);
  margin-bottom: 4px;
}

.settings-block-hint {
  font-size: 12px;
  color: var(--lulu-text-muted);
  margin: 0 0 10px;
  line-height: 1.5;
}

.settings-meta-hint {
  margin: 0;
  font-size: 12px;
  color: var(--lulu-text-muted);
  line-height: 1.5;
}

.settings-meta-hint :deep(a) {
  color: var(--lulu-link);
  font-weight: 600;
  text-decoration: none;
}

.settings-meta-hint :deep(a:hover) {
  text-decoration: underline;
}

.settings-preview-pane {
  display: flex;
}

.settings-preview-sticky {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: relative;
}

@media (min-width: 960px) {
  .settings-preview-sticky {
    position: sticky;
    top: 88px;
  }
}

/* Mobile / narrow viewports: show the live preview ABOVE the editor so
   users see their changes as they type. DOM order stays semantic; CSS
   `order` swaps the visual order only. */
@media (max-width: 959px) {
  .settings-preview-pane {
    order: 1;
  }
  /* The shared white surface now lives on the panel, so the editor pane drops
     its own card chrome (background, shadow, padding) to avoid a card-in-card. */
  .settings-editor-pane {
    order: 2;
    background: transparent;
    box-shadow: none;
    border-radius: 0;
    padding: 0;
  }
}

.settings-preview-caption {
  margin: 12px 0 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--lulu-text-muted);
  text-align: center;
}

.settings-preview-disclaimer {
  margin: 8px 0 0;
  max-width: 280px;
  font-size: 11px;
  line-height: 1.45;
  color: var(--lulu-text-muted);
  font-style: italic;
  text-align: center;
}

</style>
